/**
 * Insulation, jacket and coating takeoff for the Pipe Sketch tool.
 *
 * Thickness recommendations follow the ASHRAE 90.1 / typical mechanical-spec
 * bands for the chosen service. Surface areas use the outer face of the
 * insulation (the way an insulator measures), fittings are converted to
 * equivalent length, and coating coverage uses the standard 1 gal / 200 sq ft
 * per coat at the specified DFT.
 *
 * Deterministic tables, no lookups — works offline.
 */

import { getSizeSpec, type MaterialId } from "@/lib/pipe-spec";

export type InsulationType = "none" | "fiberglass" | "calsil" | "elastomeric" | "mineralwool";

export type ServiceBand = "chilled" | "hotLow" | "hotHigh";

export const SERVICE_BANDS: Array<{ id: ServiceBand; label: string; note: string }> = [
  { id: "chilled", label: "Cold / chilled (35–60 °F)", note: "Vapour barrier required" },
  { id: "hotLow", label: "Hot ≤ 250 °F", note: "Heating water, low-pressure steam" },
  { id: "hotHigh", label: "Hot > 250 °F", note: "Steam and high-temp process" },
];

export interface InsulationTypeMeta {
  id: InsulationType;
  label: string;
  spec: string;
  /** installed density used for weight estimating, lb/ft3 */
  density: number;
  /** typical services this product covers */
  services: ServiceBand[];
}

export const INSULATION_TYPES: InsulationTypeMeta[] = [
  { id: "none", label: "None (bare pipe)", spec: "—", density: 0, services: [] },
  {
    id: "fiberglass",
    label: "Fiberglass pipe insulation",
    spec: "ASTM C547 · ASJ jacket",
    density: 4.5,
    services: ["chilled", "hotLow", "hotHigh"],
  },
  {
    id: "calsil",
    label: "Calcium silicate",
    spec: "ASTM C533 Type I",
    density: 13,
    services: ["hotHigh"],
  },
  {
    id: "elastomeric",
    label: "Elastomeric (closed cell)",
    spec: "ASTM C534",
    density: 5,
    services: ["chilled"],
  },
  {
    id: "mineralwool",
    label: "Mineral wool",
    spec: "ASTM C547 Type IV",
    density: 8,
    services: ["hotLow", "hotHigh"],
  },
];

export function insulationLabel(id: InsulationType) {
  return INSULATION_TYPES.find((t) => t.id === id)?.label ?? id;
}

/**
 * Recommended thickness in inches by nominal size band and service.
 * Bands are [maxNominalSize, thickness].
 */
const THICKNESS: Record<ServiceBand, Array<[number, number]>> = {
  chilled: [
    [1, 0.5],
    [1.5, 1],
    [4, 1],
    [8, 1.5],
    [Infinity, 1.5],
  ],
  hotLow: [
    [1, 1],
    [1.5, 1.5],
    [4, 1.5],
    [8, 2],
    [Infinity, 2],
  ],
  hotHigh: [
    [1, 1.5],
    [1.5, 2],
    [4, 2.5],
    [8, 3],
    [Infinity, 3.5],
  ],
};

const nomOf = (nps: string) => {
  const m = nps.match(/(\d+)\s*-\s*(\d+)\/(\d+)/);
  if (m) return Number(m[1]) + Number(m[2]) / Number(m[3]);
  const f = nps.match(/^(\d+)\/(\d+)/);
  if (f) return Number(f[1]) / Number(f[2]);
  return parseFloat(nps) || 1;
};

export function recommendedThickness(nps: string, service: ServiceBand): number {
  const nom = nomOf(nps);
  for (const [max, t] of THICKNESS[service]) if (nom <= max) return t;
  return 2;
}

/** Fitting and valve covers converted to equivalent feet of straight pipe. */
const EQUIV_FT = { elbow: 1, tee: 1.5, reducer: 1, valve: 2.5 };

export interface CoatingSpec {
  id: "none" | "primer" | "primerFinish" | "hightemp";
  label: string;
  coats: number;
  /** square feet covered per gallon, per coat */
  coveragePerGal: number;
  note: string;
}

export const COATINGS: CoatingSpec[] = [
  { id: "none", label: "None", coats: 0, coveragePerGal: 1, note: "—" },
  {
    id: "primer",
    label: "Shop primer only",
    coats: 1,
    coveragePerGal: 250,
    note: "2.0 mil DFT alkyd primer",
  },
  {
    id: "primerFinish",
    label: "Primer + finish coat",
    coats: 2,
    coveragePerGal: 200,
    note: "2.0 mil primer + 2.0 mil enamel",
  },
  {
    id: "hightemp",
    label: "High-temp silicone",
    coats: 2,
    coveragePerGal: 175,
    note: "Rated to 1000 °F",
  },
];

export type CoatingId = CoatingSpec["id"];

export interface TakeoffSettings {
  insulation: InsulationType;
  service: ServiceBand;
  /** override the recommended thickness, inches; null = use the table */
  thicknessOverride: number | null;
  jacket: "none" | "asj" | "aluminum" | "stainless" | "pvc";
  coating: CoatingId;
  /** extra allowance on measured area, percent */
  wastePct: number;
}

export const DEFAULT_TAKEOFF: TakeoffSettings = {
  insulation: "fiberglass",
  service: "hotLow",
  thicknessOverride: null,
  jacket: "asj",
  coating: "primer",
  wastePct: 10,
};

export const JACKETS: Array<{ id: TakeoffSettings["jacket"]; label: string }> = [
  { id: "none", label: "No jacket" },
  { id: "asj", label: "ASJ (all-service)" },
  { id: "aluminum", label: "Aluminium, 0.016 in" },
  { id: "stainless", label: "Stainless, 0.010 in" },
  { id: "pvc", label: "PVC fitting covers" },
];

export interface InsulationLine {
  size: string;
  /** straight feet of pipe */
  feet: number;
  /** equivalent feet added by fitting and valve covers */
  equivFeet: number;
  thickness: number;
  /** insulated outer surface, sq ft (incl. waste) */
  insulArea: number;
  /** bare pipe surface for coating, sq ft (incl. waste) */
  coatArea: number;
  /** installed insulation weight, lb */
  weight: number;
}

export interface TakeoffResult {
  lines: InsulationLine[];
  totalFeet: number;
  insulArea: number;
  coatArea: number;
  insulWeight: number;
  jacketArea: number;
  /** fitting and valve covers, each */
  fittingCovers: number;
  valveCovers: number;
  coatGallons: number;
  coating: CoatingSpec;
}

export interface TakeoffInput {
  material: MaterialId;
  cutList: Array<{ size: string; cutLength: number }>;
  fittings: Array<{ size: string; kind: string }>;
  placements: Array<{ size: string }>;
  settings: TakeoffSettings;
}

export function buildTakeoff(input: TakeoffInput): TakeoffResult {
  const { material, cutList, fittings, placements, settings } = input;
  const waste = 1 + settings.wastePct / 100;
  const insulated = settings.insulation !== "none";
  const density = INSULATION_TYPES.find((t) => t.id === settings.insulation)?.density ?? 0;

  const bySize = new Map<string, { feet: number; equivFeet: number }>();
  const bump = (size: string, feet: number, equiv = 0) => {
    const cur = bySize.get(size) ?? { feet: 0, equivFeet: 0 };
    cur.feet += feet;
    cur.equivFeet += equiv;
    bySize.set(size, cur);
  };

  for (const p of cutList) bump(p.size, p.cutLength / 12);

  let fittingCovers = 0;
  for (const f of fittings) {
    const ft =
      f.kind === "tee"
        ? EQUIV_FT.tee
        : f.kind === "reducer"
          ? EQUIV_FT.reducer
          : EQUIV_FT.elbow;
    bump(f.size, 0, ft);
    fittingCovers += 1;
  }
  let valveCovers = 0;
  for (const c of placements) {
    bump(c.size, 0, EQUIV_FT.valve);
    valveCovers += 1;
  }

  const lines: InsulationLine[] = [];
  for (const [size, v] of bySize) {
    const od = getSizeSpec(material, size).od;
    const thickness = insulated
      ? (settings.thicknessOverride ?? recommendedThickness(size, settings.service))
      : 0;
    const totalFt = v.feet + v.equivFeet;
    const outerDia = od + 2 * thickness;
    const insulArea = insulated ? ((Math.PI * outerDia) / 12) * totalFt * waste : 0;
    const coatArea = ((Math.PI * od) / 12) * totalFt * waste;
    // annulus volume, ft3 -> lb
    const volFt3 = (Math.PI / 4) * ((outerDia ** 2 - od ** 2) / 144) * totalFt;
    lines.push({
      size,
      feet: v.feet,
      equivFeet: v.equivFeet,
      thickness,
      insulArea,
      coatArea,
      weight: insulated ? volFt3 * density : 0,
    });
  }
  lines.sort((a, b) => nomOf(a.size) - nomOf(b.size));

  const coating = COATINGS.find((c) => c.id === settings.coating) ?? COATINGS[0];
  const insulArea = lines.reduce((s, l) => s + l.insulArea, 0);
  const coatArea = insulated ? 0 : lines.reduce((s, l) => s + l.coatArea, 0);
  // insulated pipe is still primed underneath when a shop primer is specified
  const paintedArea = lines.reduce((s, l) => s + l.coatArea, 0);

  return {
    lines,
    totalFeet: lines.reduce((s, l) => s + l.feet + l.equivFeet, 0),
    insulArea,
    coatArea: coatArea || paintedArea,
    insulWeight: lines.reduce((s, l) => s + l.weight, 0),
    jacketArea: settings.jacket === "none" ? 0 : insulArea,
    fittingCovers: insulated ? fittingCovers : 0,
    valveCovers: insulated ? valveCovers : 0,
    coatGallons:
      coating.coats > 0 ? (paintedArea * coating.coats) / coating.coveragePerGal : 0,
    coating,
  };
}
